import React, { useContext, useRef, useState } from "react";
import { Button, Menu } from "@mui/material";
import { MuiBox } from "components/atoms";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useNavigate,
  useParams,
  matchPath,
  useLocation,
} from "react-router-dom";
import JSZip from "jszip";

import { FirebaseContext } from "context/FirebaseContext";
import { openCenterModal } from "store/slices/CenterModal.slice";
import { openToast } from "store/slices/toast.slice";
import { CheckFileExistAction, UploadFilesAction } from "store/middlewares/files";
import { uploadFileHandle, uploadFolderHandle } from "store/slices/general";
import { getFileExtension } from "utils/fileExtension";

import CreateRenameModal from "../CreateRenameModal";

import NewFolder from "assets/images/svg-icons/newfolder.svg";
import FileUpload from "assets/images/svg-icons/fileupload.svg";
import FolderUpload from "assets/images/svg-icons/folderupload.svg";
import { UploadFolderAction } from "store/middlewares/general";
import UploadOptionsModal from "../UploadOptionsModal";

const DriveCreateMenu = ({ open, anchorEl, anchorPosition, onClose, isViewOnly = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { folderId } = useParams();
  const location = useLocation();

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Prevent the same upload error toast from being displayed
  // multiple times for the same file selection batch.
  const uploadErrorRef = useRef(new Set());

  const [isZipping, setIsZipping] = useState(false);

  const { settings } = useContext(FirebaseContext);
  const { setting } = useSelector((state) => state.general);
  const { homeFilesFolder } = useSelector((state) => state.general);

  const match = matchPath("/drive/folder/:id", location.pathname);

  // ----- File Upload -----
  const handleFileClick = () => {
    onClose?.();
    fileInputRef.current?.click();
  };
  // ----- Upload helpers (check exists -> modal -> upload) -----
  const buildName = (name, i) => {
    const dot = name.lastIndexOf(".");
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : "";
    return `${base} (${i})${ext}`;
  };

  const fileExists = (fileName) =>
    dispatch(
      CheckFileExistAction({
        fileName,
        ...(folderId && { folderId }),
      }),
    ).then((result) => {
      const fileId = result?.payload?.response?.data?.id || result?.payload?.response?.file?.id || result?.payload?.response?.id;
      return {
        exists: Boolean(result?.payload?.response?.exists),
        fileId: fileId || null,
      };
    });

  const findFileIdByName = (fileName) => {
    const allFiles = [
      ...(homeFilesFolder?.my_files?.data || []),
      ...(homeFilesFolder?.shared_with_me?.data?.map(item => item?.file).filter(Boolean) || []),
    ];
    const file = allFiles.find(f => f.file_name === fileName || f.name === fileName);
    return file?.id || null;
  };

  const handleUploadError = (res) => {
    const errorKey = String(res);
    if (uploadErrorRef.current.has(errorKey)) return;
    uploadErrorRef.current.add(errorKey);

    if (res === 2005) {
      dispatch(
        openToast({
          type: "error",
          message: t("errorCode.2005", { value: settings?.maxFileSize }),
        }),
      );
    } else if (res === 2001) {
      dispatch(openToast({ type: "error", message: t("errorCode.2001") }));
    } else {
      dispatch(openToast({ type: "error", message: t("validation.uploadFailed") }));
    }
  };

  // replace = true -> backend deletes the old file and uploads the new one
  const sendFiles = (files, replace = false, oldFileId = null) => {
    if (!files.length) return;

    const filesWithTempIds = files.map((file) => ({
      tempId: crypto.randomUUID(),
      name: file.name,
      fileRef: file,
    }));

    dispatch(
      uploadFileHandle({
        files: filesWithTempIds.map(({ tempId, name, fileRef }) => ({
          tempId,
          name,
          fileRef,
          folderId: null,
          isReplaced: replace,
          oldFileId: replace ? oldFileId : null,
        })),
        display: true,
      }),
    );

    filesWithTempIds.forEach(({ tempId, fileRef }) => {
      const formData = new FormData();
      formData.append("files", fileRef);
      formData.append("type", getFileExtension(fileRef));
      if (folderId) formData.append("folderId", folderId);
      if (replace) formData.append("isReplaced", "true");

      dispatch(
        UploadFilesAction({
          formData,
          tempId,
          oldFileId: replace ? oldFileId : null,
          onSuccess: () => {
            if (!match?.params?.id) {
              navigate("/drive/mydrive");
            }
          },
          onError: handleUploadError,
        }),
      );
    });
  };

  const getRenamedFile = async (file) => {
    // First check locally in Redux state for performance
    const existingNames = new Set([
      ...(homeFilesFolder?.my_files?.data?.map(f => f.file_name || f.name) || []),
      ...(homeFilesFolder?.shared_with_me?.data?.map(item => item?.file?.file_name || item?.file?.name).filter(Boolean) || []),
    ]);

    for (let i = 1; i <= 50; i++) {
      const newName = buildName(file.name, i);
      if (!existingNames.has(newName)) {
        // Double-check with backend to be safe
        const exists = await fileExists(newName);
        if (!exists.exists) {
          return new File([file], newName, { type: file.type });
        }
      }
    }
    return file;
  };

  const uploadWithCheck = async (files) => {
    const results = await Promise.all(files.map((f) => fileExists(f.name)));
    const newFiles = files.filter((_, i) => !results[i].exists);
    const duplicates = files.filter((_, i) => results[i].exists);

    sendFiles(newFiles);

    if (duplicates.length > 0) {
      dispatch(
        openCenterModal({
          content: (
            <UploadOptionsModal
              fileName={
                duplicates.length === 1
                  ? duplicates[0].name
                  : `${duplicates.length} files`
              }
              onConfirm={async (option) => {
                uploadErrorRef.current.clear();
                if (option === "replace") {
                  duplicates.forEach((file, idx) => {
                    const originalIndex = files.indexOf(file);
                    let fileId = results[originalIndex]?.fileId;
                    // Fallback: search by name if backend doesn't return ID
                    if (!fileId) {
                      fileId = findFileIdByName(file.name);
                    }
                    sendFiles([file], true, fileId);
                  });
                } else {
                  // Rename files in parallel for better performance
                  const renamed = await Promise.all(duplicates.map(f => getRenamedFile(f)));
                  sendFiles(renamed);
                }
              }}
            />
          ),
        }),
      );
    }
  };
  const handleFileChange = (event) => {
    // Start a new error-deduplication batch for this file selection.
    uploadErrorRef.current.clear();

    const files = Array.from(event.target.files);

    const fileSizeLimit = settings?.maxFileSize;
    const maxDriveStorage = Number(setting?.max_drive_storage || 0);
    const usedDriveStorage = Number(setting?.used_drive_storage || 0);
    const remainingSpace = maxDriveStorage - usedDriveStorage;
    const fileUploadLimit = settings?.maxFileCount;

    if (files.length > fileUploadLimit) {
      dispatch(
        openToast({
          type: "error",
          message: t("validation.fileUploadLimitMsg", { value: fileUploadLimit }),
        }),
      );
      event.target.value = null;
      return;
    }

    const totalUploadSize = files.reduce((total, file) => total + file.size, 0);

    if (totalUploadSize > remainingSpace * 1024 * 1024) {
      dispatch(
        openToast({
          type: "error",
          message: t("validation.driveNotEnoughStorage", {
            required: (totalUploadSize / (1024 * 1024)).toFixed(2),
            available: remainingSpace.toFixed(2),
          }),
        }),
      );
      event.target.value = null;
      return;
    }

    const hasOversizedFile = files.some(
      (file) => file.size > fileSizeLimit * 1024 * 1024,
    );

    if (hasOversizedFile) {
      const errorKey = "file-size-limit";
      if (!uploadErrorRef.current.has(errorKey)) {
        uploadErrorRef.current.add(errorKey);
        dispatch(
          openToast({
            type: "error",
            message: t("errorCode.2005", { value: fileSizeLimit }),
          }),
        );
      }
    }

    const validFiles = files.filter(
      (file) => file.size <= fileSizeLimit * 1024 * 1024,
    );

    if (!validFiles.length) {
      event.target.value = null;
      return;
    }

    uploadWithCheck(validFiles);

    event.target.value = null;
  };

  // ----- Folder Upload (auto ZIP & download) -----
  const handleFolderClick = () => {
    onClose?.();

    // Safely trigger the hidden folder input.
    if (folderInputRef.current) {
      folderInputRef.current.click();
    }
  };

  const handleFolderChange = async (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    // --- Optional: Validate file count and total size (same as file upload) ---
    const fileUploadLimit = settings?.maxFileCount;

    if (files.length > fileUploadLimit) {
      dispatch(
        openToast({
          type: "error",
          message: t("validation.fileUploadLimitMsg", {
            value: fileUploadLimit,
          }),
        }),
      );

      event.target.value = null;
      return;
    }

    const maxDriveStorage = Number(setting?.max_drive_storage || 0);
    const usedDriveStorage = Number(setting?.used_drive_storage || 0);
    const remainingSpace = maxDriveStorage - usedDriveStorage;

    const totalUploadSize = files.reduce(
      (total, file) => total + file.size,
      0,
    );

    if (totalUploadSize > remainingSpace * 1024 * 1024) {
      dispatch(
        openToast({
          type: "error",
          message: t("validation.driveNotEnoughStorage", {
            required: (totalUploadSize / (1024 * 1024)).toFixed(2),
            available: remainingSpace.toFixed(2),
          }),
        }),
      );

      event.target.value = null;
      return;
    }

    const fileSizeLimit = settings?.maxFileSize;

    const hasOversizedFile = files.some(
      (file) => file.size > fileSizeLimit * 1024 * 1024,
    );

    if (hasOversizedFile) {
      dispatch(
        openToast({
          type: "error",
          message: t("errorCode.2005", {
            value: fileSizeLimit,
          }),
        }),
      );
    }

    const validFiles = files.filter(
      (file) => file.size <= fileSizeLimit * 1024 * 1024,
    );

    if (!validFiles.length) {
      event.target.value = null;
      return;
    }

    // --- Proceed with zipping and upload ---
    setIsZipping(true);

    const zip = new JSZip();

    // Collect all directories.
    const dirs = new Set();

    validFiles.forEach((file) => {
      const parts = file.webkitRelativePath.split("/").slice(1);

      for (let i = 0; i < parts.length - 1; i++) {
        dirs.add(parts.slice(0, i + 1).join("/"));
      }
    });

    dirs.forEach((dir) => zip.folder(dir));

    // Add files.
    validFiles.forEach((file) => {
      const relativePath = file.webkitRelativePath
        .split("/")
        .slice(1)
        .join("/");

      zip.file(relativePath, file);
    });

    const zipBlob = await zip.generateAsync({
      type: "blob",
    });

    const rootFolder =
      validFiles[0].webkitRelativePath.split("/")[0] || "folder";

    // Show the folder upload loader in the bottom corner.
    dispatch(uploadFolderHandle({
      name: rootFolder, folderData: {
        zip: zipBlob,
        parentId: folderId,
        name: rootFolder,
      },
    }));

    dispatch(
      UploadFolderAction({
        zip: zipBlob,
        parentId: folderId,
        name: rootFolder,

        onSuccess: () => {
          setIsZipping(false);
          event.target.value = null;

          if (!match?.params?.id) {
            navigate("/drive/mydrive");
          }
        },

        onError: () => {
          setIsZipping(false);
          event.target.value = null;
        },
      }),
    );
  };

  return (
    <>
      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={anchorPosition}
        slotProps={{
          paper: {
            sx: {
              mt: "8px",
              p: "16px",
              width: "100%",
              maxWidth: "230px",
              boxShadow: "0px 6px 24px 0px #0000000D",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "background.gray400",
            },
          },
        }}
      >
        <MuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* New Folder */}
          <Button
            variant={isViewOnly ? "whiteContained" : "blueContained"}
            onClick={() => {
              if (isViewOnly) return;
              dispatch(
                openCenterModal({
                  content: (
                    <CreateRenameModal
                      title={t("sidebar.newFolder")}
                      btnName={t("sidebar.create")}
                      id={folderId}
                    />
                  ),
                }),
              );

              onClose?.();
            }}
            disabled={isViewOnly}
            startIcon={<img src={NewFolder} alt="" />}
          >
            {t("sidebar.newFolder")}
          </Button>

          {/* File Upload */}
          <Button
            variant="whiteContained"
            onClick={isViewOnly ? undefined : handleFileClick}
            disabled={isViewOnly}
            startIcon={<img src={FileUpload} alt="" />}
          >
            {t("sidebar.fileUpload")}
          </Button>

          {/* Folder Upload */}
          <Button
            variant="whiteContained"
            startIcon={<img src={FolderUpload} alt="" />}
            onClick={isViewOnly ? undefined : handleFolderClick}
            disabled={isZipping || isViewOnly}
          >
            {isZipping ? t("zipping", "Zipping…") : t("sidebar.folderUpload")}
          </Button>
        </MuiBox>
      </Menu>

      {/* Hidden input for individual files */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Hidden input for folder selection */}
      <input
        ref={folderInputRef}
        type="file"
        style={{ display: "none" }}
        multiple
        {...{ webkitdirectory: "", directory: "" }}
        onChange={handleFolderChange}
      />
    </>
  );
};

export default DriveCreateMenu;
